import {
  Badge,
  BadgeProps,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@crono-one/design-system';
import { useAuth } from 'context/auth';
import CrossCircleIcon from 'crono-fe-common/icons/Icon-Cross-Circle';
import NotOwnedIcon from 'crono-fe-common/icons/Icon-Not-Owned';
import OwnerIcon from 'crono-fe-common/icons/Icon-Owner';
import PrivateLockIcon from 'crono-fe-common/icons/Icon-Private-Lock';

type UserBadgeProps = Omit<BadgeProps, 'startIcon' | 'endIcon' | 'children'> & {
  userName?: string | null;
  userId?: number | null;
  showFullName?: boolean;
  isPrivate?: boolean;
};

const IconOwner = ({ className }: { className?: string }) => (
  <OwnerIcon color="currentColor" className={className} />
);
const IconNotOwner = ({ className }: { className?: string }) => (
  <NotOwnedIcon color="currentColor" className={className} />
);
const IconNoUser = ({ className }: { className?: string }) => (
  <CrossCircleIcon color="currentColor" className={className} />
);
const IconPrivate = ({ className }: { className?: string }) => (
  <PrivateLockIcon color="currentColor" className={className} />
);

const UserBadge = ({
  userName,
  userId,
  showFullName = false,
  isPrivate = false,
  ...badgeProps
}: UserBadgeProps) => {
  const { user } = useAuth();

  const getBadgeContent = () => {
    const isCurrentUser = !!userId && user?.id === userId;

    const EndIcon = isPrivate ? IconPrivate : undefined;

    if (userId && userName) {
      const [firstName, lastName] = userName.split(' ');
      const displayedLastName = showFullName ? lastName : `${lastName?.[0]}.`;

      return {
        displayName: `${firstName} ${displayedLastName}`,
        tooltipText: userName,
        StartIcon: isCurrentUser ? IconOwner : IconNotOwner,
        EndIcon,
      };
    }

    if (userId) {
      return {
        displayName: 'Deleted User',
        tooltipText: 'User is inactive',
        StartIcon: IconNoUser,
        EndIcon,
      };
    }

    return {
      displayName: 'Non-Crono user',
      tooltipText: 'User from crm',
      StartIcon: IconNoUser,
      EndIcon,
    };
  };

  const { displayName, tooltipText, StartIcon, EndIcon } = getBadgeContent();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          size="sm"
          tone="gray"
          startIcon={StartIcon}
          endIcon={EndIcon}
          {...badgeProps}
        >
          {displayName}
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="top">{tooltipText}</TooltipContent>
    </Tooltip>
  );
};

export default UserBadge;
